/*
 * Taskpane entry: in Excel → validate stored token (GET /api/admin/auth/whoami) → login or main app;
 * in browser → VPN-gated manifest landing.
 * Restores dark mode and theme snapshot immediately (before auth check) to avoid flash.
 * Skins: `?brand=` from manifest — see `src/skins/`.
 */

/* global Office, document */

// Shared-runtime bootstrap (was a separate runtime.js URL in manifest — invalid for Excel Online).
import "../runtime/runtime"
import { createUserMenu } from "@aiapollo/design-system"
import {
	applyCssFromSnapshot,
	applyThemeFromSnapshot,
	applyPaletteToThemeBuilder,
	getThemeSnapshot,
	createThemeBuilderPage,
	computeThemeSnapshotForPalette,
} from "@aiapollo/design-system"
import { FLAT_UI_PALETTES } from "@aiapollo/design-system"
import { createDataPage } from "./pages/data/data"
import { createComponentsPage } from "./pages/components/components"
import { createLoginPage } from "./pages/login/login"
import { startBrowserLanding } from "./browserLanding"
import { isRunningInExcel } from "./excelHost"
import { getActiveSkin, getBrandConfig, skinHasLockedTheme } from "../skins"
import { storage, STORAGE_KEYS } from "./services/storageService"
import { refreshWorkbookFormulas, registerWorksheetActivationRefresh } from "./services/excelService"
import {
	getAccessToken,
	getApiBaseUrl,
	saveAccessToken,
	syncTokenToOfficeRuntimeStorage,
} from "../functions/functions"
import { isStoredAccessTokenAcceptedByApi } from "./services/authService"
import { ICON_REFRESH, ICON_LOGOUT } from "./icons"

function applyLockedSkinDarkSurfaces(skin: ReturnType<typeof getActiveSkin>): void {
	const surf = skin.lockedTheme?.darkModeSurfaces
	if (!surf) return
	const r = document.documentElement.style
	r.setProperty("--ds-color-bg-page", surf.page)
	r.setProperty("--ds-color-bg-surface", surf.surface)
	r.setProperty("--ds-color-bg-elevated", surf.elevated)
}

function applyLockedSkinTheme(isDark: boolean): void {
	const skin = getActiveSkin()
	if (!skinHasLockedTheme(skin)) return
	const { light, dark } = skin.lockedTheme
	if (isDark) {
		applyCssFromSnapshot(computeThemeSnapshotForPalette(dark))
		applyLockedSkinDarkSurfaces(skin)
	} else {
		applyCssFromSnapshot(computeThemeSnapshotForPalette(light))
	}
}

/** Re-apply light-mode semantic + background colors (after dark toggle off). */
function applyLightThemeFromStorageOrBrand(): void {
	const skin = getActiveSkin()
	if (skinHasLockedTheme(skin)) {
		applyCssFromSnapshot(computeThemeSnapshotForPalette(skin.lockedTheme.light))
		return
	}
	const snapshot = storage.get<Record<string, string>>(STORAGE_KEYS.THEME_SNAPSHOT)
	if (snapshot) applyCssFromSnapshot(snapshot)
}

Office.onReady(async () => {
	const rootEl = document.getElementById("app-root")
	if (!rootEl) return
	const root: HTMLElement = rootEl

	document.documentElement.dataset.excelAddinBrand = getBrandConfig().datasetBrand

	// ── Cosmetic restores (no auth dependency) ───────────────────────────────
	const savedDark = storage.get<boolean>(STORAGE_KEYS.DARK_MODE) ?? false
	applyDarkMode(savedDark, false)

	// ── Browser: manifest landing (VPN-gated), not login ─────────────────────
	if (!isRunningInExcel()) {
		startBrowserLanding(root)
		return
	}

	// ── Excel host: auth gate ────────────────────────────────────────────────
	function showLogin(): void {
		root.innerHTML = ""
		const loginEl = createLoginPage({
			onLogin: () => {
				root.innerHTML = ""
				buildMainApp(root, showLogin)
			},
		})
		root.appendChild(loginEl)
	}

	let token = await getAccessToken()
	let tokenTrimmed = token && String(token).trim() ? String(token).trim() : null
	if (tokenTrimmed) {
		const sessionOk = await isStoredAccessTokenAcceptedByApi(getApiBaseUrl(), tokenTrimmed)
		if (!sessionOk) {
			saveAccessToken(null)
			tokenTrimmed = null
		} else {
			syncTokenToOfficeRuntimeStorage()
		}
	}
	if (!tokenTrimmed) {
		showLogin()
	} else {
		buildMainApp(root, showLogin)
	}
})

// ── Main app ──────────────────────────────────────────────────────────────────

function buildMainApp(root: HTMLElement, onLogout: () => void): void {
	const savedDark = storage.get<boolean>(STORAGE_KEYS.DARK_MODE) ?? false
	const savedSnapshot = storage.get<Record<string, string>>(STORAGE_KEYS.THEME_SNAPSHOT)
	const skin = getActiveSkin()
	const showThemeBuilder = skin.features.themeBuilderPage
	const showPrimaryNav = skin.features.showPrimaryNavPages
	const brand = getBrandConfig()

	// ── Logo menu ─────────────────────────────────────────────────────────────
	const menuContainer = document.createElement("div")
	menuContainer.id = "app-logo-menu"
	menuContainer.className = "app-logo-menu"
	menuContainer.setAttribute("aria-label", brand.menuRootAriaLabel)

	const triggerContent = document.createElement("img")
	triggerContent.src = brand.logoUrl
	triggerContent.alt = brand.logoAlt
	triggerContent.width = 40
	triggerContent.height = 40

	const panelContent = document.createElement("div")
	panelContent.className = "app-logo-menu-dropdown-content"

	let dataItem: HTMLButtonElement | undefined
	let componentsItem: HTMLButtonElement | undefined
	let designItem: HTMLButtonElement | undefined

	if (showPrimaryNav) {
		dataItem = document.createElement("button")
		dataItem.type = "button"
		dataItem.className = "app-logo-menu-item"
		dataItem.textContent = "Data"
		panelContent.appendChild(dataItem)

		componentsItem = document.createElement("button")
		componentsItem.type = "button"
		componentsItem.className = "app-logo-menu-item"
		componentsItem.textContent = "Components"
		panelContent.appendChild(componentsItem)

		if (showThemeBuilder) {
			designItem = document.createElement("button")
			designItem.type = "button"
			designItem.className = "app-logo-menu-item"
			designItem.textContent = "Design"
			panelContent.appendChild(designItem)
		}

		const navDivider = document.createElement("div")
		navDivider.className = "app-logo-menu-divider"
		panelContent.appendChild(navDivider)
	}

	const darkSwitch = document.createElement("button")
	darkSwitch.type = "button"
	darkSwitch.className = "app-logo-menu-theme-switch-btn"
	darkSwitch.setAttribute("role", "switch")
	darkSwitch.setAttribute("aria-checked", String(savedDark))
	const darkLabel = document.createElement("span")
	darkLabel.className = "app-logo-menu-theme-label"
	darkLabel.textContent = brand.darkThemeSwitchLabel
	const darkTrack = document.createElement("span")
	darkTrack.className = "app-logo-menu-theme-track"
	darkTrack.setAttribute("aria-hidden", "true")
	darkSwitch.append(darkLabel, darkTrack)
	panelContent.appendChild(darkSwitch)

	const refreshBtn = document.createElement("button")
	refreshBtn.type = "button"
	refreshBtn.className = "app-logo-menu-item app-logo-menu-item--toggle"
	updateRefreshLabel(refreshBtn, "idle")
	panelContent.appendChild(refreshBtn)

	if (showPrimaryNav) {
		const signOutDivider = document.createElement("div")
		signOutDivider.className = "app-logo-menu-divider"
		panelContent.appendChild(signOutDivider)
	}

	const signOutBtn = document.createElement("button")
	signOutBtn.type = "button"
	signOutBtn.className = "app-logo-menu-item app-logo-menu-item--toggle app-logo-menu-item--signout"
	signOutBtn.innerHTML = `<span class="app-logo-menu-item-icon" aria-hidden="true">${ICON_LOGOUT}</span>Sign out`
	panelContent.appendChild(signOutBtn)

	const userMenu = createUserMenu({
		triggerContent,
		panelContent,
		triggerAriaLabel: brand.menuTriggerAriaLabel,
		rootClass: "app-logo-menu",
		triggerClass: "app-logo-button",
		panelClass: "app-logo-menu-dropdown",
		openClass: "is-open",
	})

	const pageContainer = document.createElement("div")
	pageContainer.className = "page-container"

	const dataPage = createDataPage()
	dataPage.className = "page is-active"
	dataPage.setAttribute("data-page", "data")
	pageContainer.appendChild(dataPage)

	if (showPrimaryNav) {
		const componentsPage = createComponentsPage()
		componentsPage.className = "page"
		componentsPage.setAttribute("data-page", "components")
		pageContainer.appendChild(componentsPage)

		if (showThemeBuilder) {
			const savedPaletteId = storage.get<string>(STORAGE_KEYS.PALETTE_ID) ?? undefined
			const designPage = createThemeBuilderPage({
				savedSnapshot,
				savedPaletteId,
				onThemeChange: (snapshot) => storage.set(STORAGE_KEYS.THEME_SNAPSHOT, snapshot),
				onPaletteChange: (id) => {
					storage.set(STORAGE_KEYS.PALETTE_ID, id)
					storage.set(STORAGE_KEYS.THEME_SNAPSHOT, getThemeSnapshot())
				},
			})
			designPage.className = "page"
			designPage.setAttribute("data-page", "design")
			pageContainer.appendChild(designPage)
		}
	}

	menuContainer.appendChild(userMenu.root)
	root.appendChild(menuContainer)
	root.appendChild(pageContainer)

	if (showThemeBuilder) {
		if (savedSnapshot) {
			applyThemeFromSnapshot(savedSnapshot)
		} else {
			const savedPaletteId = storage.get<string>(STORAGE_KEYS.PALETTE_ID) ?? undefined
			const initialPalette =
				FLAT_UI_PALETTES.find((p) => p.id === savedPaletteId) ?? FLAT_UI_PALETTES[0]
			applyPaletteToThemeBuilder(initialPalette)
		}
	} else {
		applyLockedSkinTheme(savedDark)
	}

	function showPage(pageId: string): void {
		pageContainer.querySelectorAll(".page").forEach((el) => {
			const page = el as HTMLElement
			page.classList.toggle("is-active", page.getAttribute("data-page") === pageId)
		})
		userMenu.close()
	}

	dataItem?.addEventListener("click", () => showPage("data"))
	componentsItem?.addEventListener("click", () => showPage("components"))
	designItem?.addEventListener("click", () => showPage("design"))

	darkSwitch.addEventListener("click", () => {
		const isDark = document.documentElement.getAttribute("data-theme") === "dark"
		const next = !isDark
		applyDarkMode(next, true)
		darkSwitch.setAttribute("aria-checked", String(next))
	})

	refreshBtn.addEventListener("click", () => {
		if (refreshBtn.disabled) return
		refreshBtn.disabled = true
		updateRefreshLabel(refreshBtn, "loading")
		void refreshWorkbookFormulas()
			.then((result) => {
				updateRefreshLabel(refreshBtn, result.success ? "done" : "error")
				const delay = result.success ? 1500 : 2500
				window.setTimeout(() => {
					updateRefreshLabel(refreshBtn, "idle")
					refreshBtn.disabled = false
				}, delay)
			})
			.catch(() => {
				updateRefreshLabel(refreshBtn, "error")
				window.setTimeout(() => {
					updateRefreshLabel(refreshBtn, "idle")
					refreshBtn.disabled = false
				}, 2500)
			})
	})

	signOutBtn.addEventListener("click", () => {
		saveAccessToken(null)
		onLogout()
	})

	registerWorksheetActivationRefresh()
}

function applyDarkMode(dark: boolean, persist: boolean): void {
	if (dark) {
		document.documentElement.setAttribute("data-theme", "dark")
		document.documentElement.style.removeProperty("--ds-color-bg-page")
		document.documentElement.style.removeProperty("--ds-color-bg-surface")
		document.documentElement.style.removeProperty("--ds-color-bg-elevated")
		if (skinHasLockedTheme(getActiveSkin())) {
			applyLockedSkinTheme(true)
		} else {
			const snap = storage.get<Record<string, string>>(STORAGE_KEYS.THEME_SNAPSHOT)
			if (snap) applyCssFromSnapshot(snap)
		}
	} else {
		document.documentElement.removeAttribute("data-theme")
		applyLightThemeFromStorageOrBrand()
	}
	if (persist) storage.set(STORAGE_KEYS.DARK_MODE, dark)
}

function updateRefreshLabel(
	btn: HTMLButtonElement,
	state: "idle" | "loading" | "done" | "error",
): void {
	const c = getBrandConfig()
	const label =
		state === "loading"
			? c.refreshFormulasLoading
			: state === "done"
				? c.refreshFormulasDone
				: state === "error"
					? c.refreshFormulasError
					: c.refreshFormulasIdle
	btn.innerHTML = `<span class="app-logo-menu-item-icon app-logo-menu-item-icon--refresh${state === "loading" ? " is-spinning" : ""}" aria-hidden="true">${ICON_REFRESH}</span>${label}`
}
